import {
  sortBy,
  isEmpty,
  first,
  isArray,
  map,
  isObject,
  reduce,
  get,
  truncate,
  slice,
} from 'lodash';
import { validContacts } from 'utils/contact';
import { CUSTOM_FIELD_MAX_LENGTH } from 'constants/contact';
import {
  CONTACT_CREATE,
  CONTACTS_CREATE,
  GET_CONTACTS,
  GET_ACCOUNT_CONTACTS,
  GET_CONTACT_PITCH_PREVIEWS,
  DELETE_CONTACT,
  DELETE_CONTACTS,
  CREATE_CONTACTS_BULK,
  GET_PREVIEW_CONTACTS,
  CONTACTS_CSV_SAVE,
  SET_PREVIEW_CONTACT_PARAMS, SEARCH_PREVIEW_CONTACTS,
  RESET_PREVIEW_CONTACTS,
  CREATE_CONTACT_PITCH_PREVIEWS,
  UPDATE_CONTACT_PITCH_PREVIEWS,
  RESET_CONTACTS,
  GET_CONTACT,
  UPDATE_CONTACT,
  GET_OUTLETS,
} from '../actions/contactActions';

const initialState = {
  contact: {
    loading: false,
    loaded: false,
  },
  contacts: {
    loading: false,
    loaded: true,
    sequence: null,
    account: null,
    results: [],
    errors: [],
  },
  outlets: {
    loading: false,
    loaded: false,
    results: [],
    errors: [],
  },
  contactPitchPreviews: {
    loading: false,
    loaded: true,
    steps: [],
  },
  previewContacts: {
    params: {}, // params passed to api
    previewParams: {
      page: 1,
      search: null,
      pageSize: 10,
      count: 0, // this is used for search pagination in view, which needs to remain separate from parent count
    }, // params passed to filter view
    loading: false,
    loaded: false,
    total_valid_contacts: null,
    results: [],
    contactErrors: [],
    customFields: {},
    displayResults: [],
    count: 0,
  },
  contactsCSV: [],
  accountContacts: {
    loading: true,
    loaded: false,
    id: null,
    contact: null,
    sequence: null,
    status_replied_date: null,
    status_interested_date: null,
    status_sponsorship_date: null,
    status_unsubscribed_date: null,
    status_not_interested_date: null,
    status_not_now_date: null,
    active: null,
    results: [],
  },
};

/**
 * Get error message from array
 * @param error
 * @returns {string}
 */
const getErrorMessage = error => first(first(map(error, v => v)));

/**
 * Get first key from obj
 * @param obj
 * @returns {string}
 */
const getErrorKey = obj => first(Object.keys(obj));

/**
 * Transforms a single contact error from response
 * @param responseErrors
 * @returns {{additional_data: number, error_type: [string, any]}[]}
 */
const transformError = responseErrors => map(responseErrors, (value, key) => {
  let errorMessage = '';
  let errorKey;
  if (isArray(value)) {
    errorMessage = first(value);
    if (isObject(errorMessage)) {
      errorKey = getErrorKey(errorMessage);
      errorMessage = getErrorMessage(errorMessage);
    }
  } else if (isObject(value)) {
    errorKey = getErrorKey(value);
    errorMessage = getErrorMessage(value);
  } else {
    errorMessage = value;
  }

  return {
    error_type: errorKey ? `${key}.${errorKey}` : key,
    additional_data: errorMessage,
  };
});

/**
 * Takes in contacts from request and errors from response and formats errors object.
 * @param contacts
 * @param responseErrors
 * @returns {*}
 */
const transformContactErrors = (contacts, responseErrors) => responseErrors.map((error, i) => ({
  ...contacts[i],
  id: `bulk-add-error-${i}`,
  errors: !isEmpty(error) ? transformError(error) : [],
})).filter(v => !isEmpty(v.errors));

/**
 * Gets custom_field from request, and returns with a list of email and custom_fields
 * @param requestData
 * @returns {*}
 */
const extractCustomFieldsFromRequest = requestData => requestData
  // eslint-disable-next-line
  .filter(r => !isEmpty(r.custom_field) && !isEmpty(r.email)).map(({ email, custom_field }) => ({ email: email.toLowerCase().trim(), custom_field }));

/**
 * Create's a look up by email to retrieve custom fields.
 * @param customFieldArr
 * @returns {*}
 */
const createCustomFieldLookUpByEmail = customFieldArr => reduce(
  customFieldArr,
  // eslint-disable-next-line
  (acc, { email, custom_field }) => ({ ...acc, [email]: map(custom_field, (value) => ({
    field_name: value.field_name,
    field_value: !isEmpty(value.field_value)
      ? truncate(value.field_value, { length: CUSTOM_FIELD_MAX_LENGTH })
      : value.field_value,
  // eslint-disable-next-line
  })).filter(({ field_name, field_value }) => !isEmpty(field_name) && !isEmpty(field_value)),
  }),
  {},
);

/**
 * Transforms a contact before placing into previewContact store
 * @param results
 * @param customFieldsLookup
 * @returns {*}
 */
const transformPreviewContactResults = (results, customFieldsLookup = {}) => results.map((r, i) => ({
  ...r,
  uiId: `uid-${i}`,
  ...!isEmpty(customFieldsLookup[r.email]) ? { custom_field: customFieldsLookup[r.email] } : {},
}));

/**
 * Used to paginate results set
 * @param params
 * @param results
 * @returns {Array}
 */
const sliceResults = ({ pageSize, page = 1 }, results) => slice(
  results,
  (page - 1) * pageSize,
  pageSize * page,
);

/**
 * Filters result set in redux store given an object of params
 * @param params
 * @param results
 * @returns {Array}
 */
const filterPreviewContacts = (params, results) => {
  if (isEmpty(params.search)) {
    return results;
  }
  return results.filter(contact => (!isEmpty(contact.email) && contact.email.includes(params.search))
    || (!isEmpty(contact.first_name) && contact.first_name.includes(params.search))
    || (!isEmpty(contact.last_name) && contact.last_name.includes(params.search)));
};


export const contactsCSVReducer = (state = initialState.contactsCSV, action) => {
  switch (action.type) {
    case CONTACTS_CSV_SAVE:
      return action.payload;
    default:
      return state;
  }
};

export const contactReducer = (state = initialState.contact, action) => {
  switch (action.type) {
    case GET_CONTACT.pending:
    case UPDATE_CONTACT.pending:
      return {
        ...state,
        loading: true,
        loaded: false,
      };
    case GET_CONTACT.error:
    case UPDATE_CONTACT.error:
      return {
        ...state,
        loading: false,
        loaded: true,
        ...action.payload.data,
      };
    case GET_CONTACT.success:
    case UPDATE_CONTACT.success:
      return {
        loading: false,
        loaded: true,
        ...action.payload.data,
      };
    default:
      return state;
  }
};

export const contactsReducer = (state = initialState.contacts, action) => {
  switch (action.type) {
    case CONTACT_CREATE.pending:
      return {
        ...state,
        loading: true,
        loaded: false,
      };
    case CONTACT_CREATE.error:
      return {
        ...state,
        loading: false,
        loaded: true,
      };
    case CONTACT_CREATE.success:
      return {
        ...state,
        loading: false,
        loaded: true,
        results: [
          ...state.results,
          action.payload.data,
        ],
      };
    case CONTACTS_CREATE.pending:
      return {
        ...state,
        loading: true,
        loaded: false,
      };
    case CONTACTS_CREATE.error:
      return {
        ...state,
        loading: false,
        loaded: true,
        ...action.payload.data,
      };
    case CONTACTS_CREATE.success:
      return {
        ...state,
        loading: false,
        loaded: true,
        ...action.payload.data,
      };
    case RESET_CONTACTS:
      return initialState.contacts;
    case CREATE_CONTACTS_BULK.pending:
      return {
        ...state,
        loading: true,
        loaded: false,
      };
    case CREATE_CONTACTS_BULK.error:
      return {
        ...state,
        loading: false,
        loaded: true,
        ...action.payload.data,
      };
    case CREATE_CONTACTS_BULK.success:
      return {
        ...state,
        loading: false,
        loaded: true,
        ...action.payload.data,
      };
    case DELETE_CONTACT.success:
      return {
        ...state,
        loading: false,
        loaded: true,
        results: state.results.filter(contact => contact.id !== action.payload),
      };
    case DELETE_CONTACTS.pending:
      return {
        ...state,
        loading: true,
        loaded: false,
      };
    case DELETE_CONTACTS.error:
      return {
        ...state,
        loading: false,
        loaded: true,
      };
    case DELETE_CONTACTS.success:
      return {
        ...state,
        loading: false,
        loaded: true,
        results: state.results.filter(contact => !action.payload.includes(contact.id)),
      };
    default:
      return state;
  }
};

export const outletsReducer = (state = initialState.outlets, action) => {
  switch (action.type) {
    case GET_OUTLETS.pending:
      return {
        ...state,
        loading: true,
        loaded: false,
      };
    case GET_OUTLETS.error:
      return {
        ...state,
        loading: false,
        loaded: true,
      };
    case GET_OUTLETS.success:
      if (!action.payload.data.previous) {
        return {
          ...state,
          loading: false,
          loaded: true,
          ...action.payload.data,
        };
      }
      return {
        ...state,
        loading: false,
        loaded: true,
        ...action.payload.data,
        results: [...state.results, ...action.payload.data.results],
      };
    default:
      return state;
  }
};


export const previewContactsReducer = (state = initialState.previewContacts, action) => {
  switch (action.type) {
    case GET_PREVIEW_CONTACTS.pending:
      return {
        ...state,
        loading: true,
        loaded: false,
      };
    case GET_PREVIEW_CONTACTS.error:
      return {
        ...state,
        loading: false,
        loaded: true,
        ...action.payload.data,
      };
    case DELETE_CONTACT.success:
      // eslint-disable-next-line
      const stateResults = state.results.filter(contact => contact.id !== action.payload);
      // eslint-disable-next-line
      const filteredContactsPrev = filterPreviewContacts(state.previewParams, stateResults);
      return {
        ...state,
        loading: false,
        loaded: true,
        results: stateResults,
        count: stateResults.length,
        displayResults: sliceResults(state.previewParams, filteredContactsPrev),
        previewParams: {
          ...state.previewParams,
          count: filteredContactsPrev.length,
        },
        total_valid_contacts: validContacts(stateResults).length,
      };
    case GET_PREVIEW_CONTACTS.success:
      // eslint-disable-next-line
      const results = transformPreviewContactResults(
        [...action.payload.data.results, ...state.contactErrors],
        state.customFields,
      );
      // eslint-disable-next-line
      const filteredContacts = filterPreviewContacts(state.previewParams, results);
      return {
        ...state,
        loading: false,
        loaded: true,
        results,
        count: results.length,
        displayResults: sliceResults(state.previewParams, filteredContacts),
        total_valid_contacts: validContacts(action.payload.data.results).length,
        previewParams: {
          ...state.previewParams,
          page: 1,
          count: filteredContacts.length,
        },
      };
    case CONTACTS_CREATE.pending:
      return {
        ...state,
        contactErrors: [],
      };
    case CREATE_CONTACTS_BULK.success:
      return {
        ...state,
        customFields:
          createCustomFieldLookUpByEmail(extractCustomFieldsFromRequest(JSON.parse(action.payload.config.data))),
        contactErrors: transformContactErrors(JSON.parse(action.payload.config.data), action.payload.data.errors),
      };
    case CONTACT_CREATE.success:
      return {
        ...state,
        customFields: {
          ...state.customFields,
          ...createCustomFieldLookUpByEmail(extractCustomFieldsFromRequest([JSON.parse(action.payload.config.data)])),
        },
      };
    case SEARCH_PREVIEW_CONTACTS.success:
      // eslint-disable-next-line
      const displayResults = filterPreviewContacts(
        { ...state.previewParams, ...action.payload },
        state.results,
      );
      return {
        ...state,
        previewParams: {
          ...state.previewParams,
          ...action.payload,
          count: displayResults.length,
          page: action.payload.page,
        },
        displayResults: sliceResults({ ...state.previewParams, ...action.payload }, displayResults),
      };
    case SET_PREVIEW_CONTACT_PARAMS.success:
      return {
        ...state,
        ...action.payload,
      };
    case RESET_PREVIEW_CONTACTS:
      return initialState.previewContacts;
    case CONTACTS_CREATE.success:
      return {
        ...state,
        loading: false,
        loaded: true,
        params: {
          ...state.params,
          contacts: get(action.payload, 'data.results', []),
        },
      };
    default:
      return state;
  }
};

export const accountContactsReducer = (state = initialState.accountContacts, action) => {
  switch (action.type) {
    case GET_ACCOUNT_CONTACTS.pending:
      return {
        ...state,
        loading: true,
        loaded: false,
        ...action.payload,
      };
    case GET_ACCOUNT_CONTACTS.error:
      return {
        ...state,
        loading: false,
        loaded: true,
        ...action.payload.data,
      };
    case GET_ACCOUNT_CONTACTS.success:
      return {
        ...state,
        loading: false,
        loaded: true,
        ...action.payload.data,
      };
    default:
      return state;
  }
};

export const dropdownContactsReducer = (state = initialState.accountContacts, action) => {
  switch (action.type) {
    case GET_CONTACTS.pending:
      return {
        ...state,
        loading: true,
        loaded: false,
        ...action.payload,
      };
    case GET_CONTACTS.error:
      return {
        ...state,
        loading: false,
        loaded: true,
        ...action.payload.data,
      };
    case GET_CONTACTS.success:
      return {
        ...state,
        loading: false,
        loaded: true,
        ...action.payload.data,
      };
    default:
      return state;
  }
};
export const contactPitchPreviewsReducer = (state = initialState.contactPitchPreviews, action) => {
  switch (action.type) {
    case GET_CONTACT_PITCH_PREVIEWS.success:
      return {
        ...state,
        loading: false,
        loaded: true,
        ...action.payload.data,
        steps: sortBy(action.payload.data.steps || [], ['send_from_launch_duration']),
      };
    case GET_CONTACT_PITCH_PREVIEWS.pending:
      return {
        ...state,
        loading: true,
        loaded: false,
        ...action.payload,
      };
    case GET_CONTACT_PITCH_PREVIEWS.error:
      return {
        ...state,
        loading: false,
        loaded: true,
        ...action.payload,
      };
    case CREATE_CONTACT_PITCH_PREVIEWS.pending:
      return {
        ...state,
        loading: true,
        loaded: false,
      };
    case CREATE_CONTACT_PITCH_PREVIEWS.error:
      return {
        ...state,
        loading: false,
        loaded: true,
        ...action.payload.data,
      };
    case CREATE_CONTACT_PITCH_PREVIEWS.success:
      return {
        ...state,
        loading: false,
        loaded: true,
        steps: state.steps.map(s => ({
          ...s,
          pitches: s.pitches.map((p) => {
            const obj = action.payload.data.find(el => el.parent === p.id);
            return { ...p, ...{ ...obj, collapsed: false } };
          }),
        })),
      };
    case UPDATE_CONTACT_PITCH_PREVIEWS.pending:
      return {
        ...state,
        loading: true,
        loaded: false,
      };
    case UPDATE_CONTACT_PITCH_PREVIEWS.error:
      return {
        ...state,
        loading: false,
        loaded: true,
        ...action.payload.data,
      };
    case UPDATE_CONTACT_PITCH_PREVIEWS.success:
      return {
        ...state,
        loading: false,
        loaded: true,
        steps: state.steps.map(s => ({
          ...s,
          pitches: s.pitches.map((p) => {
            const obj = action.payload.data.find(el => el.id === p.id);
            return { ...p, ...{ ...obj, collapsed: false } };
          }),
        })),
      };
    default:
      return state;
  }
};
